import {Component} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {AuthService} from "../../../_services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {catchError, filter, first, of} from "rxjs";
import {User} from "../../../_models/user";
import {UsersService} from "../../../_services/users.service";
import {emailValidatorByPattern, minLengthValidator, requiredValidator} from "../../../_util/validators";
import {GlobalStore} from "../../../global.store";
import {TargetCompanyService} from "../../../_services/target-company.service";
import {CompaniesService} from "../../../_services/companies.service";
import {HttpErrorResponse} from "@angular/common/http";
import {CompanyWorkspaceService} from "../../../_services/company-workspace.service";

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['../auth.scss'],
    standalone: false
})
export class LoginComponent{
  readonly defaultTitle = 'Welcome to Color Job';
  title = this.defaultTitle;
  loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [emailValidatorByPattern(), requiredValidator()]),
    password: new FormControl('', [minLengthValidator('PASSWORD'), requiredValidator()]),
    remember_me: new FormControl()
  })
  isShowPassword: boolean = false;
  returnUrl: string;
  submitted: boolean = false;
  error: boolean = false;
  errorMessage = 'Invalid email or password';
  showLoader = false;

  constructor(private authService: AuthService,
              private router: Router,
              private route: ActivatedRoute,
              private userService: UsersService,
              readonly globalStore: GlobalStore,
              private targetCompanyService: TargetCompanyService,
              private companiesService: CompaniesService,
              private companyWorkspaceService: CompanyWorkspaceService) {
    this.route.queryParams.subscribe((params) => {
      this.returnUrl = params['returnUrl']?.toLowerCase();
      const targetCompanyId = this.targetCompanyService.getTargetCompanyId(params['companyId'], this.returnUrl);
      this.targetCompanyService.setTargetCompanyId(targetCompanyId);
    });
    this.loadCompanyTitle();
    this.authService.makeLogoutRequest().subscribe();
  }

  onSubmit() {
    if (this.loginForm.invalid) return;

    this.showLoader = true;

    const remember_me = this.loginForm.get("remember_me")?.value === true ? 'on' : 'off';
    const companyId = this.targetCompanyService.getTargetCompanyId(null, this.returnUrl);
    const loginData = {...this.loginForm.value, remember_me, companyId}
    this.authService.login(loginData).pipe(first()).subscribe((data : User) => {
      const userCompanyId = this.userService.getPrimaryCompanyId(data);
      if (userCompanyId) this.targetCompanyService.setTargetCompanyId(userCompanyId);
      else this.targetCompanyService.clearTargetCompanyId();

      this.globalStore.currentUser$.pipe(filter(user => !!user), first()).subscribe(() => {
        this.globalStore.resetState();
        console.log(1)

        if (!this.returnUrl) {
          const destination = this.companyWorkspaceService.resolvePostLoginDestination(data);
          this.companyWorkspaceService.navigateToDestination(destination);
          return;
        }
        else this.returnUrl = this.returnUrl.replace('system/', '').replace('/view', '/builder');

        this.returnUrl = this.returnUrl.replace('noteid', 'noteId');
        console.log(3)
        console.log(this.returnUrl)
        this.router.navigateByUrl(this.returnUrl);
      })
    },
    (error: HttpErrorResponse) => {
      this.submitted = true;
      this.error = true;
      this.errorMessage = this.resolveLoginErrorMessage(error);
      this.showLoader = false;
    });
  }

  private resolveLoginErrorMessage(error: HttpErrorResponse): string {
    const backendCode = error?.error?.code;
    const backendMessage = error?.error?.error || error?.error?.message || error?.error;

    if (backendCode === 'USER_NOT_IN_TARGET_COMPANY') {
      return 'You are not a member of this company.';
    }

    if (typeof backendMessage === 'string' && backendMessage.trim().length > 0) {
      const normalizedMessage = backendMessage.trim().toLowerCase();

      if (
        normalizedMessage.includes('not a member of this company')
        || normalizedMessage.includes('does not belong to target company')
        || normalizedMessage.includes('does not belong to the target company')
      ) {
        return 'You are not a member of this company.';
      }

      return backendMessage.trim();
    }

    return 'Invalid email or password';
  }

  private loadCompanyTitle(): void {
    const subdomain = this.getCurrentSubdomain();
    if (!subdomain || subdomain.toLowerCase() === 'app') {
      this.title = this.defaultTitle;
      return;
    }

    this.companiesService.getCompanyBySubdomain(subdomain).pipe(
      first(),
      catchError(() => of(null))
    ).subscribe((company) => {
      if (!company?.name) {
        this.title = this.defaultTitle;
        return;
      }

      this.title = `Welcome to ${company.name}`;
      this.targetCompanyService.setTargetCompanyId(company.id);
    });
  }

  private getCurrentSubdomain(): string | null {
    const hostname = window.location.hostname;
    const mainDomain = 'colorjob.com';

    if (!hostname.endsWith(`.${mainDomain}`)) return null;

    const subdomain = hostname.substring(0, hostname.length - mainDomain.length - 1);
    return subdomain && !subdomain.includes('.') ? subdomain : null;
  }
}
